(() => {
  if (customElements.get('sega-category-motion')) return;
  const menuSections = new Map();
  const menuContent = (section) => {
    if (!menuSections.has(section)) {
      const url = new URL(location.href);
      url.searchParams.set('section_id', section);
      menuSections.set(section, fetch(url).then((response) => {
        if (!response.ok) throw new Error('Menu images unavailable');
        return response.text();
      }).then((html) => {
        const template = document.createElement('template');
        template.innerHTML = html;
        const decorations = new Map();
        template.content.querySelectorAll('[data-menu-decoration-key]:not(sega-menu-decoration)').forEach((element) => {
          decorations.set(element.dataset.menuDecorationKey, element);
        });
        if (!decorations.size) throw new Error('Menu images unavailable');
        return decorations;
      }).catch((error) => {
        setTimeout(() => menuSections.delete(section), 5000);
        throw error;
      }));
    }
    return menuSections.get(section);
  };
  const menuObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => { if (entry.isIntersecting) entry.target.hydrate(); });
  }) : null;
  class MenuDecoration extends HTMLElement {
    connectedCallback() {
      this.details = [];
      for (let parent = this.parentElement; parent; parent = parent.parentElement) {
        if (parent.tagName === 'DETAILS') this.details.push(parent);
      }
      this.onToggle = () => {
        const bounds = this.getBoundingClientRect();
        if (bounds.width && bounds.bottom > 0 && bounds.top < innerHeight && bounds.right > 0 && bounds.left < innerWidth) this.hydrate();
      };
      this.details.forEach((detail) => detail.addEventListener('toggle', this.onToggle));
      if (menuObserver) menuObserver.observe(this);
      else this.hydrate();
    }
    disconnectedCallback() {
      menuObserver?.unobserve(this);
      this.details?.forEach((detail) => detail.removeEventListener('toggle', this.onToggle));
    }
    async hydrate() {
      if (this.loading) return;
      if (this.details.some((detail) => !detail.open && !detail.querySelector(':scope > summary')?.contains(this))) return;
      this.loading = true;
      try {
        const decorations = await menuContent(this.dataset.menuSection);
        const decoration = decorations.get(this.dataset.menuDecorationKey);
        if (decoration && this.isConnected) this.replaceWith(decoration.cloneNode(true));
      } catch {
        this.loading = false;
      }
    }
  }
  customElements.define('sega-menu-decoration', MenuDecoration);
  const members = new Set();
  const subscribers = new Set();
  const images = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce), (max-width: 749px)');
  const frameInterval = 400;
  const galleryInterval = frameInterval * 6;
  let timer;
  let paused = false;
  const load = (url) => {
    if (!images.has(url)) images.set(url, new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = url;
    }));
    return images.get(url);
  };
  const tick = () => {
    timer = undefined;
    const now = performance.now();
    const framePhase = Math.floor(now / frameInterval);
    const galleryPhase = Math.floor(now / galleryInterval);
    const galleries = [...members].filter((member) => member.running() && member.frames.length > 1);
    galleries.forEach((member) => member.prepareFrame(galleryPhase % member.frames.length));
    const galleriesReady = galleries.every((member) => {
      const frame = member.frames[galleryPhase % member.frames.length];
      return frame.complete && frame.naturalWidth > 0;
    });
    let active = false;
    members.forEach((member) => {
      if (!member.running()) return;
      member.advance(framePhase, galleryPhase, galleriesReady);
      if (member.dataset.kind === 'athlete' || member.frames.length > 1) active = true;
    });
    subscribers.forEach((subscriber) => {
      if (!subscriber.running()) return;
      subscriber.advance(now, framePhase, galleryPhase);
      active = true;
    });
    if (active) timer = setTimeout(tick, frameInterval - performance.now() % frameInterval);
  };
  let syncQueued = false;
  const schedule = () => {
    if (syncQueued) return;
    syncQueued = true;
    queueMicrotask(() => {
      syncQueued = false;
      clearTimeout(timer);
      timer = undefined;
      members.forEach((member) => member.sync());
      if ([...members, ...subscribers].some((member) => member.running())) tick();
    });
  };
  window.segastoreMotionClock = Object.freeze({
    frameInterval,
    galleryInterval,
    schedule,
    subscribe(subscriber) {
      subscribers.add(subscriber);
      schedule();
      return () => { subscribers.delete(subscriber); schedule(); };
    }
  });
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => { entry.target.inViewport = entry.isIntersecting; });
    schedule();
  }, { threshold: .05 }) : null;
  class CategoryMotion extends HTMLElement {
    connectedCallback() {
      this.frames = [...this.querySelectorAll('[data-motion-frame]')];
      this.atlas = this.querySelector('.segastore-category-motion__atlas');
      this.inViewport = !observer;
      this.index = 0;
      this.ancestors = [];
      for (let parent = this.parentElement; parent; parent = parent.parentElement) {
        if (parent.tagName === 'DETAILS') this.ancestors.push(parent);
      }
      this.onToggle = () => schedule();
      this.ancestors.forEach((detail) => detail.addEventListener('toggle', this.onToggle));
      members.add(this);
      observer?.observe(this);
      schedule();
    }
    disconnectedCallback() {
      members.delete(this);
      observer?.unobserve(this);
      this.ancestors?.forEach((detail) => detail.removeEventListener('toggle', this.onToggle));
      schedule();
    }
    visible() {
      return this.isConnected && this.inViewport && this.ancestors.every((detail) => detail.open || detail.querySelector(':scope > summary')?.contains(this)) && !this.closest('[hidden]');
    }
    running() { return this.visible() && !document.hidden && !reduced.matches && !paused && !this.failed; }
    sync() {
      const active = String(this.running());
      if (this.dataset.active !== active) this.dataset.active = active;
      if (this.visible() && this.dataset.sheet && !this.loading && !this.ready && !this.failed) {
        this.loading = true;
        const sheets = [this.dataset.sheet, this.dataset.alternateSheet].filter(Boolean);
        Promise.all(sheets.map((url) => load(url).then(() => url).catch(() => null))).then((loaded) => {
          if (!this.isConnected) return;
          this.sheets = loaded.filter(Boolean);
          if (!this.sheets.length) { this.failed = true; this.dataset.active = 'false'; return; }
          this.atlas.style.backgroundImage = `url(${JSON.stringify(this.sheets[0])})`;
          this.ready = true;
          this.dataset.ready = 'true';
          schedule();
        }).catch(() => { this.failed = true; this.dataset.active = 'false'; });
      }
      if (this.visible() && this.frames.length) {
        if (reduced.matches || paused) this.prepareFrame(this.index);
        else this.frames.forEach((frame, index) => this.prepareFrame(index));
      }
      if (reduced.matches && this.index !== 0) {
        this.index = 0;
        this.frames.forEach((frame, i) => frame.classList.toggle('is-current', i === 0));
        this.dataset.frame = '0';
      }
    }
    prepareFrame(index) {
      const frame = this.frames[index];
      if (!frame || frame.src || !frame.dataset.src) return;
      frame.src = frame.dataset.src;
    }
    advance(framePhase, galleryPhase, galleriesReady) {
      if (this.ready && this.atlas) {
        const sheetIndex = Math.floor(framePhase / 12) % this.sheets.length;
        const sheet = this.sheets[sheetIndex];
        if (this.currentSheet !== sheet) {
          this.atlas.style.backgroundImage = `url(${JSON.stringify(sheet)})`;
          this.currentSheet = sheet;
          this.dataset.sheetIndex = String(sheetIndex);
        }
        const frame = framePhase % 6;
        this.atlas.style.backgroundPosition = `${(frame % 3) * 50}% ${frame < 3 ? 0 : 100}%`;
        this.dataset.frame = String(frame);
      }
      if (this.frames.length < 2) return;
      const next = galleryPhase % this.frames.length;
      if (!galleriesReady || next === this.index) return;
      this.frames.forEach((frame, i) => frame.classList.toggle('is-current', i === next));
      this.index = next;
      this.dataset.frame = String(next);
    }
  }
  customElements.define('sega-category-motion', CategoryMotion);
  document.addEventListener('visibilitychange', schedule);
  reduced.addEventListener('change', schedule);
  const controls = () => {
    const buttons = document.querySelectorAll('[data-sport-mark-toggle]');
    const readPause = () => { paused = [...buttons].some((button) => button.getAttribute('aria-pressed') === 'true'); schedule(); };
    const controlObserver = new MutationObserver(readPause);
    buttons.forEach((button) => controlObserver.observe(button, { attributes: true, attributeFilter: ['aria-pressed'] }));
    readPause();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', controls, { once: true });
  else controls();
})();
